/**
 * Seam Crossing Rush — pure game engine (no React, no DOM, no randomness beyond
 * a seeded LCG).
 *
 * An agent crossing a governed seam is constructable only when it carries its
 * complete required witness tuple. A crossing missing any witness component is
 * unconstructable — there is no crossing arrow for it, so the forbidden shape is
 * structurally inexpressible, not blocked at the seam. This reducer is the single
 * source of truth for the "does it cross" verdict: the player assembles each
 * request's witness tuple from a palette, then dispatches it. A complete, exact
 * tuple crosses by construction and scores; an incomplete or off-tuple dispatch
 * is refused as structurally inexpressible and the agent does not cross.
 *
 * Spawning is deterministic given a seed and a fixed tick sequence so the model
 * is reproducible and testable. The arcade layer (combo, flashes, clock) lives
 * in the panel and never alters the structural verdict computed here.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export type WitnessId = string;

/** A witness component the player can attach to the active request. */
export interface WitnessComponent {
  readonly id: WitnessId;
  readonly label: string;
  readonly glyph: string;
  readonly summary: string;
}

/** A template for the kind of crossing requests a wave spawns. */
export interface CrossingTemplate {
  readonly id: string;
  readonly title: string;
  /** The exact witness tuple this crossing requires to be constructable. */
  readonly required: readonly WitnessId[];
  /** Ticks of patience before the agent gives up and the crossing is missed. */
  readonly patienceTicks: number;
}

/** A wave (level) of the game: its palette, request templates, and cadence. */
export interface Wave {
  readonly id: string;
  readonly title: string;
  readonly brief: string;
  readonly palette: readonly WitnessComponent[];
  readonly templates: readonly [CrossingTemplate, ...CrossingTemplate[]];
  /** Ticks between successive spawns. Lower is faster (harder). */
  readonly spawnEveryTicks: number;
  /** Number of crossings to construct to clear the wave. */
  readonly quota: number;
}

/** A live crossing request approaching the seam, awaiting its witness tuple. */
export interface CrossingRequest {
  readonly id: string;
  readonly templateId: string;
  readonly title: string;
  readonly required: readonly WitnessId[];
  readonly attached: readonly WitnessId[];
  /** Ticks of patience remaining before the request times out (is missed). */
  readonly patienceRemaining: number;
}

export type CrossingVerdict = "constructable" | "unconstructable";

export interface RushState {
  readonly waveIndex: number;
  readonly seed: number;
  /** Tick counter; spawning is a pure function of this and the seed. */
  readonly tick: number;
  /** Tick at which the next spawn is due. */
  readonly nextSpawnTick: number;
  /** Monotonic counter giving each spawned request a stable id. */
  readonly spawnCount: number;
  readonly queue: readonly CrossingRequest[];
  /** Index into the queue of the request the palette acts on (the front). */
  readonly activeId: string | null;
  readonly score: number;
  readonly crossed: number;
  readonly refused: number;
  readonly missed: number;
  readonly status: string;
  readonly lastVerdict: CrossingVerdict | null;
}

export type RushIntent =
  | { readonly kind: "tick" }
  | { readonly kind: "attach"; readonly witnessId: WitnessId }
  | { readonly kind: "detach"; readonly witnessId: WitnessId }
  | { readonly kind: "dispatch" }
  | { readonly kind: "reset" }
  | { readonly kind: "next" };

// ── Seeded RNG — a small pure LCG (no Math.random anywhere in the engine). ──

const LCG_A = 1664525;
const LCG_C = 1013904223;

/** One deterministic step of the LCG, returning the next 32-bit state. */
function lcgNext(state: number): number {
  return (Math.imul(state, LCG_A) + LCG_C) >>> 0;
}

/**
 * Deterministically pick a template index for a given spawn ordinal and seed.
 * Pure: identical (seed, spawnCount) always yields the same choice, so the
 * spawn stream is reproducible for tests.
 */
export function templateIndexFor(
  seed: number,
  spawnCount: number,
  templateCount: number,
): number {
  if (templateCount <= 0) return 0;
  const salt = ((spawnCount + 1) * 2654435761) >>> 0;
  let s = (seed ^ salt) >>> 0;
  s = lcgNext(s);
  s = lcgNext(s);
  return s % templateCount;
}

// ── Pure predicates: the structural verdict lives here. ──

/**
 * A witness tuple is complete iff the attached set equals the required tuple
 * exactly — no missing component and no extra one. Order does not matter.
 */
export function tupleComplete(
  required: readonly WitnessId[],
  attached: readonly WitnessId[],
): boolean {
  if (required.length !== attached.length) return false;
  const held = new Set(attached);
  if (held.size !== attached.length) return false;
  return required.every((id) => held.has(id));
}

/**
 * The single source of truth: a crossing is constructable iff its attached set
 * equals its required witness tuple exactly. Otherwise the crossing arrow does
 * not exist — the crossing is unconstructable, structurally inexpressible.
 */
export function crossingConstructable(request: CrossingRequest): boolean {
  return tupleComplete(request.required, request.attached);
}

/** Points a constructed crossing is worth, before any arcade combo scaling. */
export function dispatchScore(request: CrossingRequest): number {
  return crossingConstructable(request)
    ? 100 + request.required.length * 20
    : 0;
}

// ── Helpers ──

function activeRequest(state: RushState): CrossingRequest | null {
  if (state.activeId === null) return null;
  return state.queue.find((r) => r.id === state.activeId) ?? null;
}

function firstQueuedId(queue: readonly CrossingRequest[]): string | null {
  return queue.at(0)?.id ?? null;
}

function paletteForWave(wave: Wave): readonly WitnessComponent[] {
  return wave.palette;
}

function templateById(wave: Wave, templateId: string): CrossingTemplate | null {
  return wave.templates.find((t) => t.id === templateId) ?? null;
}

export function waveByIndex(
  waves: readonly Wave[],
  index: number,
): Wave | null {
  return waves.at(index) ?? null;
}

export function activeOf(state: RushState): CrossingRequest | null {
  return activeRequest(state);
}

export function paletteOf(
  waves: readonly Wave[],
  state: RushState,
): readonly WitnessComponent[] {
  const wave = waveByIndex(waves, state.waveIndex);
  return wave === null ? [] : paletteForWave(wave);
}

export function waveCleared(waves: readonly Wave[], state: RushState): boolean {
  const wave = waveByIndex(waves, state.waveIndex);
  return wave !== null && state.crossed >= wave.quota;
}

/**
 * Classify a pure transition for the arcade layer (combo / flash), without the
 * panel re-deriving structural facts. "cross" = a crossing was constructed;
 * "refuse" = a dispatch was refused as structurally inexpressible; "miss" = a
 * request timed out; "none" = no scoring-relevant change.
 */
export function arcadeEvent(
  prev: RushState,
  next: RushState,
): "cross" | "refuse" | "miss" | "none" {
  if (next.crossed > prev.crossed) return "cross";
  if (next.refused > prev.refused) return "refuse";
  if (next.missed > prev.missed) return "miss";
  return "none";
}

// ── Initial state and reducer ──

export function initialState(waves: readonly Wave[]): RushState {
  return freshWave(waves, 0, 1);
}

function freshWave(
  waves: readonly Wave[],
  waveIndex: number,
  seed: number,
): RushState {
  const wave = waveByIndex(waves, waveIndex);
  return {
    waveIndex,
    seed,
    tick: 0,
    nextSpawnTick: 0,
    spawnCount: 0,
    queue: [],
    activeId: null,
    score: 0,
    crossed: 0,
    refused: 0,
    missed: 0,
    status:
      wave === null
        ? "No wave loaded."
        : `${wave.title} — bring each crossing its complete witness tuple before it reaches the seam.`,
    lastVerdict: null,
  };
}

function spawnRequest(wave: Wave, state: RushState): CrossingRequest {
  const index = templateIndexFor(
    state.seed,
    state.spawnCount,
    wave.templates.length,
  );
  const [head] = wave.templates;
  const template = wave.templates.at(index) ?? head;
  return {
    id: `${wave.id}-req-${String(state.spawnCount)}`,
    templateId: template.id,
    title: template.title,
    required: template.required,
    attached: [],
    patienceRemaining: template.patienceTicks,
  };
}

/**
 * Advance the world by one tick: age every request's patience, drop and count
 * any that timed out (missed — the agent simply cannot cross without its tuple),
 * and spawn a new request when one is due. Pure and deterministic.
 */
function stepTick(wave: Wave, state: RushState): RushState {
  const tick = state.tick + 1;

  const aged = state.queue.map((r) => ({
    ...r,
    patienceRemaining: r.patienceRemaining - 1,
  }));
  const survivors = aged.filter((r) => r.patienceRemaining > 0);
  const timedOut = aged.length - survivors.length;

  let queue = survivors;
  let nextSpawnTick = state.nextSpawnTick;
  let spawnCount = state.spawnCount;

  if (tick >= state.nextSpawnTick && state.crossed < wave.quota) {
    const spawned = spawnRequest(wave, { ...state, spawnCount });
    queue = [...queue, spawned];
    spawnCount += 1;
    nextSpawnTick = tick + wave.spawnEveryTicks;
  }

  const activeId =
    state.activeId !== null && queue.some((r) => r.id === state.activeId)
      ? state.activeId
      : firstQueuedId(queue);

  const missed = state.missed + timedOut;
  const status =
    timedOut > 0
      ? `${String(timedOut)} crossing(s) missed — without its witness tuple the agent cannot cross.`
      : state.status;

  return {
    ...state,
    tick,
    nextSpawnTick,
    spawnCount,
    queue,
    activeId,
    missed,
    status,
  };
}

function withActive(
  state: RushState,
  mapper: (request: CrossingRequest) => CrossingRequest,
): readonly CrossingRequest[] {
  return state.queue.map((r) => (r.id === state.activeId ? mapper(r) : r));
}

export function reducer(
  waves: readonly Wave[],
  state: RushState,
  intent: RushIntent,
): RushState {
  const wave = waveByIndex(waves, state.waveIndex);
  if (wave === null) return state;

  switch (intent.kind) {
    case "reset":
      return freshWave(waves, state.waveIndex, state.seed);

    case "next": {
      const nextIndex = state.waveIndex + 1;
      if (waveByIndex(waves, nextIndex) === null) return state;
      return freshWave(waves, nextIndex, state.seed + 1);
    }

    case "tick":
      return stepTick(wave, state);

    case "attach": {
      const active = activeRequest(state);
      if (active === null) return state;
      if (!paletteForWave(wave).some((c) => c.id === intent.witnessId)) {
        return state;
      }
      if (active.attached.includes(intent.witnessId)) return state;
      const queue = withActive(state, (r) => ({
        ...r,
        attached: [...r.attached, intent.witnessId],
      }));
      return { ...state, queue };
    }

    case "detach": {
      const active = activeRequest(state);
      if (active === null) return state;
      if (!active.attached.includes(intent.witnessId)) return state;
      const queue = withActive(state, (r) => ({
        ...r,
        attached: r.attached.filter((id) => id !== intent.witnessId),
      }));
      return { ...state, queue };
    }

    case "dispatch": {
      const active = activeRequest(state);
      if (active === null) return state;
      const constructable = crossingConstructable(active);
      const remaining = state.queue.filter((r) => r.id !== active.id);
      const nextActive = firstQueuedId(remaining);
      if (constructable) {
        const template = templateById(wave, active.templateId);
        const label = template?.title ?? active.title;
        return {
          ...state,
          queue: remaining,
          activeId: nextActive,
          score: state.score + dispatchScore(active),
          crossed: state.crossed + 1,
          status: `${label} crossed — its witness tuple is complete, so the crossing is constructable by construction.`,
          lastVerdict: "constructable",
        };
      }
      // The tuple is incomplete or off-tuple: the crossing arrow does not exist.
      // The request is refused as structurally inexpressible. Clear the attempt
      // so the player can try again; the agent stays in the queue, it does not
      // cross.
      const queue = withActive(state, (r) => ({ ...r, attached: [] }));
      return {
        ...state,
        queue,
        refused: state.refused + 1,
        status:
          "No crossing arrow exists — the witness tuple is incomplete, so the crossing is structurally inexpressible, not blocked at the seam.",
        lastVerdict: "unconstructable",
      };
    }
  }
}
